// src/components/ia-paca/IaPacaHero.tsx
//
// Sprint IA #1 — Hero premium pour les pages du hub /ia-paca/.
// Direction artistique : sobre, technique, brand pink/orange + accent
// violet IA. Anti-bullshit : pas de "leader n°1", pas de robot stock.

import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Calendar } from 'lucide-react';

interface IaPacaHeroProps {
  eyebrow?: string;
  title: string;
  highlight?: string;
  subtitle: string;
  primaryCta?: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
  badges?: string[];
}

export default function IaPacaHero({
  eyebrow = 'RECRUTEMENT IA · PROVENCE-ALPES-CÔTE D\'AZUR',
  title,
  highlight,
  subtitle,
  primaryCta = { label: 'Confier un recrutement IA', to: '/reservation' },
  secondaryCta = { label: 'Diagnostic gratuit en 15 min', to: '/reservation' },
  badges,
}: IaPacaHeroProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-violet-50/40 py-16 lg:py-24">
      {/* Halos décoratifs subtils — pas de neon, pas de particles */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-pink-300/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-violet-300/20 blur-3xl"
      />

      <div className="relative mx-auto max-w-6xl px-6">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-violet-700 backdrop-blur">
          <Sparkles className="h-3 w-3" />
          {eyebrow}
        </div>

        {/* Titre */}
        <h1 className="mt-6 max-w-4xl text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
          {title}
          {highlight && (
            <>
              {' '}
              <span className="bg-gradient-to-r from-pink-600 via-orange-500 to-violet-600 bg-clip-text text-transparent">
                {highlight}
              </span>
            </>
          )}
        </h1>

        {/* Subtitle */}
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600 sm:text-xl">
          {subtitle}
        </p>

        {/* CTAs */}
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to={primaryCta.to}
            className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-600 to-orange-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-pink-200/50 transition hover:shadow-xl"
          >
            {primaryCta.label}
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
          </Link>
          <Link
            to={secondaryCta.to}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
          >
            <Calendar className="h-4 w-4" />
            {secondaryCta.label}
          </Link>
        </div>

        {/* Trust badges */}
        {badges && badges.length > 0 && (
          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500">
            <span className="font-semibold uppercase tracking-wider text-slate-400">
              Notre terrain
            </span>
            {badges.map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 ring-1 ring-slate-200"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
